import { Routes } from '@angular/router';
import { anonymousOnlyGuard } from '@app/core/guards/anonymous-only.guard';
import { authGuard } from '@app/core/guards/auth.guard';
import { corporateTreeGuard } from '@app/core/guards/corporate-tree.guard';
import { guestGuard } from '@app/core/guards/guest.guard';
import { kycRequiredGuard } from '@app/core/guards/kyc-required.guard';
import { maintenanceGuard } from '@app/core/guards/maintenance.guard';
import { onboardingStepGuard } from '@app/core/guards/onboarding-step.guard';
import { partialBlockGuard } from '@app/core/guards/partial-block.guard';
import { roleGuard } from '@app/core/guards/role.guard';
import { transferWithdrawBlockGuard } from '@app/core/guards/transfer-withdraw-block.guard';
import { ClientRole } from '@app/core/models';

// CB — Client Blocking (2026-06-30, AD-049 / CB-LD-9). Every non-KYC
// authenticated client route layers `partialBlockGuard` after the
// existing route-level guards (`roleGuard`, etc.). The transfers +
// withdrawals route groups additionally apply `transferWithdrawBlockGuard`.
// The `/kyc` route is intentionally NOT gated — a partially_blocked
// user must remain able to reach their verification page (CB-LD-10).
const SHARED_FEATURE_ROUTES: Routes = [
  // The /forbidden page exists for explicit access-denied UX flows, but
  // there is NO automatic redirect to it (per the 2026-04-05 hardening
  // decision — see docs/project-decisions.md). roleGuard redirects to
  // /dashboard on role mismatch, and the error.interceptor surfaces 403s
  // as on-page errors. Do NOT re-add an automatic /forbidden redirect
  // anywhere without revisiting that decision.
  {
    path: 'forbidden',
    canActivate: [partialBlockGuard],
    loadComponent: () => import('./features/forbidden/forbidden').then((m) => m.ForbiddenComponent),
    data: { title: 'Access Denied', subtitle: 'Insufficient permissions' },
  },
  {
    path: 'dashboard',
    canActivate: [partialBlockGuard],
    loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.DashboardComponent),
    data: { title: 'Dashboard', subtitle: 'Overview of your portfolio' },
  },
  {
    path: 'accounts',
    canActivate: [partialBlockGuard],
    loadComponent: () => import('./features/accounts/accounts').then((m) => m.AccountsComponent),
    data: { title: 'Account Settings', subtitle: 'Manage your trust accounts' },
  },
  {
    path: 'deposits',
    // 2026-06-08: corporate sub-users without INITIATE_DEPOSIT are
    // redirected away. Personal clients pass through implicitly (see
    // AuthService.hasPermission). Detail view shares the same gate so
    // a sub-user can't deep-link to a deposit record.
    // CB: deposits stay open under transfer_withdraw_blocked.
    canActivate: [roleGuard, partialBlockGuard],
    data: { requiredPermission: 'INITIATE_DEPOSIT' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/deposits/deposits').then((m) => m.DepositsComponent),
        data: { title: 'Deposits', subtitle: 'Fund your account' },
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./features/deposits/deposit-detail/deposit-detail').then(
            (m) => m.DepositDetailComponent,
          ),
        data: { title: 'Deposit Detail', subtitle: 'View deposit information' },
      },
    ],
  },
  {
    path: 'transfers',
    // 2026-06-08: corporate sub-users without INITIATE_TRANSFER are
    // redirected away. Personal clients pass through implicitly.
    // CB-LD-11: gated for both partially_blocked + transfer_withdraw_blocked.
    canActivate: [roleGuard, partialBlockGuard, transferWithdrawBlockGuard],
    data: { requiredPermission: 'INITIATE_TRANSFER' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/transfers/transfers').then((m) => m.TransfersComponent),
        data: { title: 'Transfers', subtitle: 'Send funds securely' },
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./features/transfers/transfer-detail/transfer-detail').then(
            (m) => m.TransferDetailComponent,
          ),
        data: { title: 'Transfer Detail', subtitle: 'View transfer information' },
      },
    ],
  },
  {
    path: 'withdrawals',
    // 2026-06-08: Withdrawals are gated by INITIATE_TRANSFER too —
    // mirrors the sidebar nav grouping in nav-items.ts.
    // CB-LD-11: gated for both partially_blocked + transfer_withdraw_blocked.
    canActivate: [roleGuard, partialBlockGuard, transferWithdrawBlockGuard],
    data: { requiredPermission: 'INITIATE_TRANSFER' },
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/withdrawals/withdrawals').then((m) => m.WithdrawalsComponent),
        data: { title: 'Withdrawals', subtitle: 'Withdraw your funds' },
      },
      {
        path: ':id',
        loadComponent: () =>
          import('./features/withdrawals/withdrawal-detail/withdrawal-detail').then(
            (m) => m.WithdrawalDetailComponent,
          ),
        data: { title: 'Withdrawal Detail', subtitle: 'View withdrawal information' },
      },
    ],
  },
  {
    path: 'history',
    canActivate: [partialBlockGuard],
    loadComponent: () => import('./features/history/history').then((m) => m.HistoryComponent),
    data: { title: 'Transaction History', subtitle: 'View all transactions' },
  },
  {
    path: 'beneficiaries',
    redirectTo: 'accounts',
    pathMatch: 'full' as const,
  },
  {
    path: 'profile',
    canActivate: [partialBlockGuard],
    loadComponent: () => import('./features/profile/profile').then((m) => m.ProfileComponent),
    data: { title: 'Profile', subtitle: 'Your information' },
  },
  {
    // CB-LD-10: /kyc is intentionally NOT gated by partialBlockGuard —
    // a partially_blocked user must remain able to reach verification.
    path: 'kyc',
    loadComponent: () => import('./features/kyc/kyc').then((m) => m.KycComponent),
    data: { title: 'KYC/AML', subtitle: 'Verification status' },
  },
  {
    path: 'settings',
    canActivate: [partialBlockGuard],
    loadComponent: () => import('./features/settings/settings').then((m) => m.SettingsComponent),
    data: { title: 'Settings', subtitle: 'Account preferences' },
  },
  {
    path: 'support',
    canActivate: [partialBlockGuard],
    loadComponent: () => import('./features/support/support').then((m) => m.SupportComponent),
    data: { title: 'Help & Support', subtitle: 'Get assistance' },
  },
];

export const routes: Routes = [
  // 2026-06-04 — landing page hidden; root now redirects straight to the
  // auth flow. The landing component file is preserved (not deleted) so
  // we can revive it later by restoring the original loadComponent route.
  {
    path: '',
    redirectTo: '/auth/login',
    pathMatch: 'full',
  },

  // Login — guest-only (redirect authenticated users to dashboard).
  {
    path: 'auth',
    canActivate: [guestGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login').then((m) => m.LoginComponent),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },

  // Password flows — PUBLIC (no guestGuard). Email-link recipients may be
  // authenticated on the device; blocking them would prevent account setup.
  // CR-1 fix: extracted from the guestGuard parent per spec §9.1.
  {
    path: 'auth/forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password').then(
        (m) => m.ForgotPasswordComponent,
      ),
  },
  {
    path: 'auth/reset-password',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'auth/setup-password',
    loadComponent: () =>
      import('./features/auth/setup-password/setup-password').then(
        (m) => m.SetupPasswordComponent,
      ),
  },

  // Maintenance page (public -- shown when backend returns 503)
  {
    path: 'maintenance',
    loadComponent: () =>
      import('./features/maintenance/maintenance').then((m) => m.MaintenanceComponent),
  },

  // Legal pages — PUBLIC (no auth guard). Opened in new tab from the
  // cross-platform consent modal (CW-13h follow-up 2026-05-21). Sample
  // placeholder copy; final content pending from Normax Capital.
  {
    path: 'terms-and-conditions',
    loadComponent: () =>
      import('./features/legal/terms-and-conditions/terms-and-conditions').then(
        (m) => m.TermsAndConditionsComponent,
      ),
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./features/legal/privacy-policy/privacy-policy').then(
        (m) => m.PrivacyPolicyComponent,
      ),
  },

  // Onboarding wizard (CW-13e). Anonymous-only — authenticated users
  // are redirected to /dashboard. The legacy single-page
  // `/onboarding/register` route was deleted in CW-13e Prompt 3 — the
  // new flow is account-type → personal/profile → personal/source-of-funds
  // (or corporate/company-info → corporate/business-details →
  // corporate/source-of-wealth).
  {
    path: 'onboarding',
    children: [
      {
        path: 'account-type',
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
          import('./features/onboarding/account-type/account-type-page').then(
            (m) => m.AccountTypePageComponent,
          ),
      },
      {
        path: 'personal/profile',
        canActivate: [anonymousOnlyGuard, onboardingStepGuard],
        loadComponent: () =>
          import(
            './features/onboarding/personal/personal-profile-page/personal-profile-page'
          ).then((m) => m.PersonalProfilePageComponent),
      },
      {
        path: 'personal/source-of-funds',
        canActivate: [anonymousOnlyGuard, onboardingStepGuard],
        loadComponent: () =>
          import(
            './features/onboarding/personal/personal-source-of-funds-page/personal-source-of-funds-page'
          ).then((m) => m.PersonalSourceOfFundsPageComponent),
      },
      {
        path: 'corporate/company-info',
        canActivate: [anonymousOnlyGuard, onboardingStepGuard],
        loadComponent: () =>
          import(
            './features/onboarding/corporate/corporate-company-info-page/corporate-company-info-page'
          ).then((m) => m.CorporateCompanyInfoPageComponent),
      },
      {
        path: 'corporate/business-details',
        canActivate: [anonymousOnlyGuard, onboardingStepGuard],
        loadComponent: () =>
          import(
            './features/onboarding/corporate/corporate-business-details-page/corporate-business-details-page'
          ).then((m) => m.CorporateBusinessDetailsPageComponent),
      },
      {
        path: 'corporate/source-of-wealth',
        canActivate: [anonymousOnlyGuard, onboardingStepGuard],
        loadComponent: () =>
          import(
            './features/onboarding/corporate/corporate-source-of-wealth-page/corporate-source-of-wealth-page'
          ).then((m) => m.CorporateSourceOfWealthPageComponent),
      },
      { path: '', redirectTo: 'account-type', pathMatch: 'full' },
    ],
  },

  // Personal Client Layout (routes without /corporate prefix)
  // kycRequiredGuard runs after authGuard — it whitelists /kyc and /auth/logout
  // so a blocked user can still reach the verification page and sign out
  // (see kyc-required.guard.ts).
  {
    path: '',
    // corporateTreeGuard runs before kycRequiredGuard so a corporate user
    // who lands on a personal-tree URL is redirected to the /corporate
    // mirror first (keeping them in the corporate layout shell).
    canActivate: [authGuard, maintenanceGuard, corporateTreeGuard, kycRequiredGuard],
    loadComponent: () =>
      import('./layouts/client-layout/client-layout').then((m) => m.ClientLayoutComponent),
    children: [
      ...SHARED_FEATURE_ROUTES,
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },

  // Corporate Client Layout (routes with /corporate prefix)
  {
    path: 'corporate',
    canActivate: [authGuard, maintenanceGuard, kycRequiredGuard],
    loadComponent: () =>
      import('./layouts/client-layout/client-layout').then((m) => m.ClientLayoutComponent),
    children: [
      ...SHARED_FEATURE_ROUTES,
      // Corporate-only routes
      {
        path: 'approvals',
        // CB-LD-9: partialBlockGuard layered after roleGuard.
        canActivate: [roleGuard, partialBlockGuard],
        loadComponent: () =>
          import('./features/approvals/approvals').then((m) => m.ApprovalsComponent),
        // 2026-06-08: closes the CW-10 frontend permission gap. A
        // CORPORATE_USER without APPROVE_REQUEST is now redirected to
        // /corporate/dashboard before the component mounts — they no
        // longer see an in-page 403. CORPORATE_OWNER passes through via
        // the implicit-allow rule in AuthService.hasPermission(). Backend
        // RBAC remains the authoritative permission boundary; this guard
        // is defence-in-depth for UX.
        data: {
          title: 'Approvals',
          subtitle: 'Pending approval queue',
          requiredRole: [ClientRole.CORPORATE_OWNER, ClientRole.CORPORATE_USER],
          requiredPermission: 'APPROVE_REQUEST',
        },
      },
      {
        path: 'users',
        // CB-LD-9: partialBlockGuard layered after roleGuard.
        canActivate: [roleGuard, partialBlockGuard],
        loadComponent: () => import('./features/users/users').then((m) => m.UsersComponent),
        data: {
          title: 'Users & Roles',
          subtitle: 'Manage team access',
          requiredRole: ClientRole.CORPORATE_OWNER,
        },
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },

  // Catch-all
  { path: '**', redirectTo: 'auth/login' },
];
